<template>
    <div v-bind="$attrs">
        <e-button v-if="type === 'button'" type="is-primary" icon-left="plus" :disabled="disabled" @click="openModal = true">
            {{ buttonLabel }}
        </e-button>
        <div
            v-if="type === 'taglist'"
            class="course-code-select__control"
            :class="{ 'course-code-select__control--error': hasError }"
        >
            <e-taglist>
                <e-tag
                    v-for="courseCode in displayedCourseCodes"
                    :key="courseCode.id"
                    class="tw:min-w-0"
                    size="is-small"
                    closable
                    :tooltip="courseCode.name"
                    aria-close-label="Close tag"
                    @close="remove(courseCode)"
                >
                    {{ courseCode.name }}
                </e-tag>

                <span v-if="additionalTagsCount > 0" class="mr-2 tw:text-surface-700 dark:tw:text-surface-300">... and {{ additionalTagsCount }} more</span>

                <Button
                    v-if="alreadySelected.length < limit || limit === -1"
                    :disabled="disabled"
                    variant="text"
                    icon="pi pi-plus-circle"
                    size="small"
                    @click="openModal = true"
                />
            </e-taglist>
        </div>

        <teleport to="body">
            <modal-select
                ref="modalSelect"
                label="Course Code"
                :open="openModal"
                :limit="limit === -1 ? null : limit"
                :filters="filters"
                :sorts="sorts"
                endpoint="course-codes"
                :available-view="['detail']"
                :already-selected="alreadySelected"
                :unselectable="unselectable"
                base-view="detail"
                @chosen="save"
                @close="close"
            >
                <template #detail-view="{ items, selected, selectItem, selectAllState, selectAllDisabled, selectAllDisabledReason }">
                    <div class="column is-full is-narrow has-text-left">
                        <e-table
                            ref="table"
                            :checked-rows="selected"
                            :data="items"
                            checkable
                            :focusable="limit === 1"
                            :header-checkable="limit !== 1"
                            :header-checkbox-state="selectAllState"
                            :header-checkbox-disabled="selectAllDisabled"
                            :header-checkbox-disabled-tooltip="selectAllDisabledReason"
                            :is-row-checkable="row => !unselectable.includes(row.id)"
                            backend-sorting
                            @check="(selected, row) => selectItem(row)"
                            @select="row => !unselectable.includes(row.id) && selectItem(row)"
                        >
                            <e-table-column v-slot="{ row: courseCode }" ref="name" field="name" label="Name" sortable>
                                {{ courseCode.name }}
                            </e-table-column>

                            <e-table-column
                                v-if="withDescription"
                                v-slot="{ row: courseCode }"
                                ref="description"
                                field="description"
                                label="Description"
                                sortable
                            >
                                {{ courseCode.description }}
                            </e-table-column>

                            <e-table-column
                                v-if="withCategory"
                                v-slot="{ row: courseCode }"
                                ref="category"
                                field="category"
                                label="Category"
                                sortable
                            >
                                {{ courseCode.category ? courseCode.category.name : '' }}
                            </e-table-column>
                        </e-table>
                    </div>
                </template>
            </modal-select>
        </teleport>
    </div>
</template>

<script>
import uniqBy from 'lodash/uniqBy.js';

import ModalSelect from '@/components/modal-select/base/ModalSelect.vue';
import TextFilter from '@/components/modal-select/filters/TextFilter.js';
import QueryParamSyncForModalSelectMixin from '@/mixins/QueryParamSyncForModalSelectMixin.js';
import { syncModalSelectRemoval } from '@/utilities/syncModalSelectRemoval.js';

export default {
    name: 'CourseCodeSelect',

    components: { ModalSelect },

    mixins: [QueryParamSyncForModalSelectMixin],

    props: {
        type: {
            type: String,
            required: false,
            default: 'taglist',
        },

        buttonLabel: {
            type: String,
            required: false,
            default: '',
        },

        withDescription: {
            type: Boolean,
            required: false,
            default: false,
        },

        withCategory: {
            type: Boolean,
            required: false,
            default: true,
        },

        open: {
            type: Boolean,
            required: false,
            default: false,
        },

        label: {
            type: String,
            required: false,
            default: 'Groups',
        },

        alreadySelected: {
            type: Array,
            required: false,
            default: () => [],
        },

        limit: {
            type: Number,
            required: false,
            default: 10,
        },

        hasError: {
            type: Boolean,
            required: false,
            default: false,
        },

        enableQueryParams: {
            type: Boolean,
            required: false,
            default: false,
        },

        urlParam: {
            type: String,
            required: false,
            default: '',
        },

        endpoint: {
            type: String,
            required: false,
            default: 'course-codes',
        },

        unselectable: {
            type: Array,
            required: false,
            default: () => [],
        },

        maxDisplay: {
            type: Number,
            required: false,
            default: 5,
        },

        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    emits: ['save', 'close', 'remove'],

    data: () => ({
        openModal: false,
        filters: [new TextFilter('name')],
        sorts: ['name'],
        selectedFromQuery: [],
        selectedRows: [],
    }),

    computed: {
        selectedCourseCodes() {
            return this.alreadySelected.length > 0 ? this.alreadySelected : this.selectedFromQuery;
        },

        displayedCourseCodes() {
            return this.selectedCourseCodes?.slice(0, this.maxDisplay) || [];
        },

        additionalTagsCount() {
            return this.selectedCourseCodes.length > this.maxDisplay
                ? this.selectedCourseCodes.length - this.maxDisplay
                : 0;
        },
    },

    created() {
        if (this.enableQueryParams) {
            this.fillFromQueryParams();
        }
    },

    beforeUnmount() {
        this.openModal = false;
    },

    methods: {
        save(chosen) {
            if (this.enableQueryParams) {
                this.setQueryParams(chosen);
            }

            const visible = this.$refs.modalSelect.items;

            // Sync the "alreadySelected" prop with the chosen course codes, but also explicitly remove the course codes that are visible in the list but not selected

            const allChosen = [...this.alreadySelected, ...chosen];
            const chosenCourseCodes = uniqBy(allChosen, 'id').filter(courseCode => {
                if (
                    visible.map(item => item.id).includes(courseCode.id) &&
                    !chosen.map(item => item.id).includes(courseCode.id)
                ) {
                    return false;
                }

                return true;
            });

            this.$emit('save', chosenCourseCodes);
        },

        close() {
            this.openModal = false;

            this.$emit('close');
        },

        remove(chosen) {
            syncModalSelectRemoval(this, chosen, this.selectedCourseCodes);
        },
    },
};
</script>

<style lang="scss" scoped>
@use './select-control' as *;

@include modal-select-control('course-code-select');
</style>
